import * as TzLookup from 'tz-lookup';
import { ActivatedRoute } from '@angular/router';
import { Activity } from '~/core/model/activity.model';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { SuccessStatusEnum } from '~/core/enums/success-status.enum';
import { TranslateService } from '@ngx-translate/core';
import { mergeMap } from 'rxjs/operators';
import { repeatUntil } from '~/core/operators/repeat-until.operator';

import { CtaButtonTypeEnum } from '~/component/call-to-action/enums/button-type.enum';
import { ModalComponent } from '~/component/modal/modal.component';
import { OrganizerSharedActivityService } from '~/organizer/shared/services/activity/activity.service';
import { OrganizerSharedLocatableService } from '~/organizer/shared/services/locatable/locatable.service';
import { VtmnPlayButtonSizeEnum } from '~/component/buttons/vtmn-play-button/enum/vtmn-play-button-size.enum';
import { VtmnPlayButtonVariantEnum } from '~/component/buttons/vtmn-play-button/enum/vtmn-play-button-variant.enum';
import { VtmnPlayButtonShapeEnum } from '~/component/buttons/vtmn-play-button/enum/vtmn-play-button-shape.enum';
import { VtmnPlayIconTypeEnum } from '~/component/vtmn-play-icon/enums/vtmn-play-icon-type.enum';
import { TipsVariantEnum } from '~/component/tips/enum/variant.enum';

@Component({
  selector: 'app-organizer-activity-edition-address',
  templateUrl: './address.component.html',
})
export class OrganizerActivityEditionAddressComponent
  implements OnInit, OnDestroy
{
  @ViewChild('saveModal')
  public saveModal: ModalComponent;

  private readonly subscriptions = new Subscription();

  public activity: Activity;
  public tzActualAddress;
  public address: FormControl;
  public isFormValid: boolean;
  public isOverlayDisplayed = false;
  public overlayStatus = SuccessStatusEnum.LOADING;

  public readonly vtmnPlayButtonShape = VtmnPlayButtonShapeEnum;
  public readonly vtmnPlayButtonSize = VtmnPlayButtonSizeEnum;
  public readonly vtmnPlayButtonVariant = VtmnPlayButtonVariantEnum;
  public readonly iconType = VtmnPlayIconTypeEnum;
  public readonly ctaButtonType = CtaButtonTypeEnum;
  public readonly tipsVariant = TipsVariantEnum;

  constructor(
    private readonly activatedRoute: ActivatedRoute,
    private readonly locatableService: OrganizerSharedLocatableService,
    private readonly organizerSharedActivityService: OrganizerSharedActivityService,
    private readonly translateService: TranslateService
  ) {}

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public ngOnInit(): void {
    this.address = new FormControl('', [Validators.required]);

    this.subscriptions.add(
      this.address.valueChanges.subscribe(() => {
        this.isFormValid =
          this.address.valid && this.address.dirty && this.sameTimezone;
      })
    );

    this.subscriptions.add(
      this.activatedRoute.data.subscribe(({ activityData }) => {
        this.activity = activityData.activity;
        this.tzActualAddress = TzLookup(
          this.activity?.location?.geo?.latitude,
          this.activity?.location?.geo?.longitude
        );
        this.address.setValue(this.activity.location);
      })
    );
  }

  public get hasInitialValue(): boolean {
    return Boolean(this.activity?.location);
  }

  public get differentTimezoneModalText(): string {
    return this.translateService.instant('checkout.cart.alert.new.text');
  }

  public get sameTimezone(): boolean {
    return (
      TzLookup(
        this.address?.value?.geo?.latitude,
        this.address?.value?.geo?.longitude
      ) === this.tzActualAddress
    );
  }

  public updateAddress(): void {
    this.isOverlayDisplayed = true;
    this.organizerSharedActivityService
      .patchActivityById(this.activity.sku, {
        location: {
          ...this.address.value,
          precision: [
            this.locatableService.buildCurrentLocatableBody(
              this.address.value.precision
            ),
          ],
        },
      })
      .pipe(
        mergeMap(() =>
          this.organizerSharedActivityService
            .getActivity(this.activity?.sku?.toString())
            .pipe(
              repeatUntil(
                (activity) =>
                  JSON.stringify({
                    precision: activity?.location?.precision ?? '',
                    identifier: activity?.location?.identifier,
                  }) ===
                  JSON.stringify({
                    precision: this.address?.value?.precision ?? '',
                    identifier: this.address?.value?.identifier,
                  })
              )
            )
        )
      )
      .subscribe(() => {
        this.overlayStatus = SuccessStatusEnum.SUCCESS;
      });
  }
}
